import React, { useEffect, useState } from 'react';
import { getCustomerPolicies } from '../../services/addService.service';
import { formatDate } from '../../utils/dateUtils';
import { X } from 'lucide-react';

function displayValue(obj, key) {
  // If the field exists but is null, show 'N/A'. If it doesn't exist, show ''.
  return Object.prototype.hasOwnProperty.call(obj, key)
    ? (obj[key] === null ? 'N/A' : obj[key])
    : '';
}

const CustomerDetailsModal = ({ visible, onClose, details }) => {
  const [policy, setPolicy] = useState({});
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyError, setPolicyError] = useState('');

  const { customer = {}, vehicle = {} } = details || {};
  const customerId = customer.id || customer.customer_id;

  useEffect(() => {
    if (visible && customerId) {
      setPolicyLoading(true);
      setPolicyError('');
      getCustomerPolicies(customerId)
        .then((res) => {
          // Use the first policy if available from the policies array
          setPolicy(res.policies?.[0] || {});
        })
        .catch(() => setPolicyError('Failed to load policy details'))
        .finally(() => setPolicyLoading(false));
    } else if (!visible) {
      setPolicy({});
      setPolicyError('');
    }
  }, [visible, customerId]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-drk-900 bg-opacity-30">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] relative">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 rounded-t-lg border-b">
          <span className="text-lg font-semibold text-black">{displayValue(customer, 'name') || 'Customer Details'}</span>
          <button onClick={onClose} className="bg-red-500 text-white rounded py-1 px-1 hover:text-white focus:outline-none">
          <X className="w-5 h-5" />
          </button>
        </div>
        <div className="pt-4 pb-5 pr-4 pl-6">
          <div className='max-h-[60vh] pt-0 overflow-y-auto pr-3'>
          <div className='border rounded-md p-4 mb-4'> 
          {/* Customer Personal Details */}
          <h2 className="text-xl font-semibold mb-4 bg-gray-50 p-2 rounded-md">Customer Personal Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-1">
            <div>
              <label className="block text-sm font-normal mb-1">Name:</label>
              <input className="w-full px-3 py-2 border rounded bg-gray-100" value={displayValue(customer, 'name')} disabled />
            </div>
            <div>
              <label className="block text-sm font-normal mb-1">Mobile Number:</label>
              <input className="w-full px-3 py-2 border rounded bg-gray-100" value={displayValue(customer, 'mobile_number')} disabled />
            </div>
            <div>
              <label className="block text-sm font-normal mb-1">Email:</label>
              <input className="w-full px-3 py-2 border rounded bg-gray-100" value={displayValue(customer, 'email')} disabled />
            </div>
          </div>
          </div>
          {/* Policy Details */}
          <div className='border rounded-md p-4 mb-4'> 
          <h2 className="text-xl font-semibold mb-4 bg-gray-50 p-2 rounded-md">Policy Details</h2>
          {policyLoading ? (
            <div className="text-blue-700 mb-4">Loading policy details...</div>
          ) : policyError ? (
            <div className="text-red-600 mb-4">{policyError}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-1">
              <div>
                <label className="block text-sm font-normal mb-1">Policy Type:</label>
                <input className="w-full px-3 py-2 border rounded bg-gray-100" value={displayValue(policy, 'policy_type')} disabled />
              </div>
              <div>
                <label className="block text-sm font-normal mb-1">Policy Start Date:</label>
                <input className="w-full px-3 py-2 border rounded bg-gray-100" value={policy.start_date ? formatDate(policy.start_date) : displayValue(policy, 'start_date')} disabled />
              </div>
              <div>
                <label className="block text-sm font-normal mb-1">Policy End Date:</label>
                <input className="w-full px-3 py-2 border rounded bg-gray-100" value={policy.end_date ? formatDate(policy.end_date) : displayValue(policy, 'end_date')} disabled />
              </div>
              <div>
                <label className="block text-sm font-normal mb-1">Policy is Valid:</label>
                <input className="w-full px-3 py-2 border rounded bg-gray-100" value={policy.is_active ? 'Yes' : policy.is_active === false ? 'No' : displayValue(policy, 'is_active')} disabled />
              </div>
            </div>
          )}
          </div>
          <div className='border rounded-md p-4 mb-4'> 
          {/* Vehicle Details */}
          <h2 className="text-xl font-semibold mb-4 bg-gray-50 p-2 rounded-md">Vehicle Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-normal mb-1">Vehicle Make:</label>
              <input className="w-full px-3 py-2 border rounded bg-gray-100" value={displayValue(vehicle, 'make')} disabled />
            </div>
            <div>
              <label className="block text-sm font-normal mb-1">Vehicle Model:</label>
              <input className="w-full px-3 py-2 border rounded bg-gray-100" value={displayValue(vehicle, 'model')} disabled />
            </div>
            <div>
              <label className="block text-sm font-normal mb-1">Registered Number:</label>
              <input className="w-full px-3 py-2 border rounded bg-gray-100" value={displayValue(vehicle, 'registration_number')} disabled />
            </div>
          </div>
          </div>
          <div className="flex justify-center mt-8">
            <button onClick={onClose} className="bg-blue-700 text-white px-8 py-2 rounded font-semibold hover:bg-blue-800">CLOSE</button>
          </div>
        </div>
        </div>
        
      </div>
    </div>
  );
};

export default CustomerDetailsModal; 